import { Link } from "react-router-dom";
import { usePublicData } from "@/lib/publicData";
import { resolveMedia } from "@/lib/api";
import Seo from "@/components/Seo";
import { Container, Reveal, SectionHeading, Eyebrow, CtaBand, PHOTOS, SERVICE_ICONS } from "@/components/public/parts";
import {
  ArrowRight, HeartHandshake, CheckCircle2, Quote, Star, ShieldCheck, Clock, Users,
  Phone, MapPin, ClipboardList, Handshake, CalendarCheck, Heart, ArrowUpRight,
} from "lucide-react";

const STEPS = [
  { icon: Phone, title: "Reach Out", text: "Submit an inquiry or referral online or by phone." },
  { icon: ClipboardList, title: "Assessment", text: "We learn about goals, needs and preferences." },
  { icon: Handshake, title: "Personalized Plan", text: "A person-centered support plan is created together." },
  { icon: CalendarCheck, title: "Ongoing Support", text: "Compassionate care and services begin — and grow." },
];

export default function Home() {
  const data = usePublicData();
  if (!data) return <div className="min-h-[60vh]" />;
  const s = data.settings || {};
  const home = (data.pages || {}).home || {};
  const sec = data.sections || {};
  const areas = s.service_areas || ["Columbus", "Cleveland", "Cincinnati"];
  const img = (k, fb) => resolveMedia(sec[k]?.image_url || fb);

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Organization", name: "Love For Mankind", url: "https://www.loveformankindhhc.com", logo: s.brand?.logo_url ? resolveMedia(s.brand.logo_url) : undefined, telephone: s.phone, email: s.email, address: { "@type": "PostalAddress", streetAddress: "600 Cross Pointe Rd", addressLocality: "Gahanna", addressRegion: "OH", postalCode: "43230", addressCountry: "US" }, areaServed: areas.map((a) => ({ "@type": "City", name: a })) },
    { "@context": "https://schema.org", "@type": "WebSite", name: "Love For Mankind", url: "https://www.loveformankindhhc.com" },
  ];

  return (
    <>
      <Seo title={home.seo_title || s.seo?.default_title} description={home.meta_description || s.seo?.default_description} path="/" image={s.brand?.logo_url} favicon={s.brand?.favicon_url} jsonLd={jsonLd} />

      {/* Hero */}
      <Container className="pt-16 lg:pt-24 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <Reveal className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-primary">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" /> Serving {areas.join(", ")}, Ohio
          </span>
          <h1 className="font-heading text-[2.6rem] sm:text-6xl lg:text-[4.3rem] font-bold tracking-tight leading-[1.05] mt-6">
            {home.h1 || "Compassionate Support. Greater Independence."}
          </h1>
          <p className="text-muted-foreground text-lg lg:text-[19px] mt-6 max-w-[600px] leading-[1.7]">
            Personalized services designed to help individuals live, grow, connect, and thrive in their communities — delivered with genuine human connection.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/request-services"><button className="rounded-full bg-primary text-primary-foreground font-semibold h-13 min-h-[52px] px-8 hover:bg-primary/90 transition-colors flex items-center gap-2" data-testid="hero-request-btn">Request Services <ArrowRight className="w-4 h-4" /></button></Link>
            <Link to="/referral"><button className="rounded-full border-2 border-foreground/15 font-semibold h-13 min-h-[52px] px-8 hover:border-primary hover:text-primary transition-colors" data-testid="hero-referral-btn">Make a Referral</button></Link>
          </div>
          <div className="flex items-center gap-8 mt-12">
            {[["18+", "Services"], [String(areas.length), "Ohio Cities"], ["100%", "Background Checked"]].map(([n, l]) => (
              <div key={l}><div className="font-heading text-3xl font-bold text-primary">{n}</div><div className="text-[13px] text-muted-foreground uppercase tracking-wider mt-1">{l}</div></div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="relative rounded-[2rem] overflow-hidden shadow-xl aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
            <img src={img("hero", PHOTOS.hero)} alt="A Love For Mankind caregiver supporting an elderly client" className="w-full h-full object-cover" />
            <div className="absolute bottom-5 left-5 right-5 bg-cream/95 backdrop-blur rounded-2xl p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shrink-0"><HeartHandshake className="w-6 h-6 text-primary-foreground" /></div>
              <p className="font-heading font-semibold leading-tight">Professional care delivered with genuine human connection.</p>
            </div>
          </div>
        </Reveal>
      </Container>

      {/* Trust bar */}
      <section className="border-y border-border bg-card">
        <Container className="py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[[ShieldCheck, "Background-checked team"], [Clock, "Reliable & on time"], [Users, "Person-centered care"], [Heart, "Community rooted"]].map(([Ic, t], i) => (
            <div key={i} className="flex items-center gap-3"><Ic className="w-6 h-6 text-gold shrink-0" /><span className="text-[15px] font-medium">{t}</span></div>
          ))}
        </Container>
      </section>

      {/* Problem / Solution */}
      <Container className="py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal className="rounded-[2rem] overflow-hidden aspect-[4/3]"><img src={img("about", PHOTOS.community)} alt="Community members supporting one another" className="w-full h-full object-cover" /></Reveal>
        <Reveal delay={0.1}>
          <SectionHeading eyebrow="Why It Matters" title="Everyone deserves to live with dignity and choice" sub="Navigating disability services can be overwhelming. Love For Mankind makes it simpler — with compassionate people who listen, advocate, and build support around each individual." />
          <ul className="mt-6 space-y-3.5">
            {["Individualized, person-centered plans", "Compassionate, trained caregivers", "Safe, accessible transportation", "Advocacy for those who need it most"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-[17px]"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> {t}</li>
            ))}
          </ul>
          <Link to="/about" className="inline-flex items-center gap-1.5 text-primary font-semibold mt-7" data-testid="home-about-link">Learn about our mission <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
      </Container>

      {/* Services */}
      <section className="bg-card border-y border-border">
        <Container className="py-20 lg:py-28">
          <Reveal><SectionHeading eyebrow="What We Do" title="Support services built around each person" /></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mt-14">
            {data.services.map((svc, i) => {
              const Icon = SERVICE_ICONS[svc.icon] || Heart;
              return (
                <Reveal key={svc.id} delay={i * 0.05}>
                  <Link to={`/services/${svc.slug}`} className="group block" data-testid="home-service">
                    <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-5 group-hover:bg-primary transition-colors"><Icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" /></div>
                    <h3 className="font-heading text-2xl font-semibold flex items-center gap-1.5">{svc.name}<ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" /></h3>
                    <p className="text-[17px] text-muted-foreground mt-2.5 leading-[1.7] line-clamp-3">{svc.description}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12"><Link to="/services"><button className="rounded-full border-2 border-foreground/15 font-semibold h-12 px-7 hover:border-primary hover:text-primary transition-colors">View all services</button></Link></div>
        </Container>
      </section>

      {/* How it works */}
      <Container className="py-20 lg:py-28">
        <Reveal><SectionHeading eyebrow="How It Works" title="A simple, supportive path to care" gold /></Reveal>
        <div className="grid md:grid-cols-4 gap-6 mt-14">
          {STEPS.map((st, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><st.icon className="w-6 h-6" /></div>
                <span className="absolute top-0 right-0 font-heading text-5xl font-bold text-secondary -z-0">{i + 1}</span>
                <h3 className="font-heading text-xl font-semibold mt-4">{st.title}</h3>
                <p className="text-[15px] text-muted-foreground mt-1.5 leading-[1.6]">{st.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>

      {/* Transportation */}
      <section className="bg-foreground text-cream">
        <Container className="py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <Eyebrow gold>Getting There</Eyebrow>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mt-3">Safe, reliable, accessible transportation</h2>
            <p className="text-cream/80 text-[17px] mt-6 leading-[1.75]">Our wheelchair-accessible fleet and trained drivers make every ride to appointments, work and community activities dignified and on time.</p>
            <Link to="/request-services"><button className="rounded-full bg-primary text-primary-foreground font-semibold h-13 min-h-[52px] px-8 mt-8 hover:bg-primary/90 transition-colors flex items-center gap-2" data-testid="home-transport-cta">Request a Ride <ArrowRight className="w-4 h-4" /></button></Link>
          </Reveal>
          <Reveal delay={0.1} className="rounded-[2rem] overflow-hidden aspect-[4/3]"><img src={img("transportation", PHOTOS.transport)} alt="A Love For Mankind wheelchair-accessible transportation van" className="w-full h-full object-cover" /></Reveal>
        </Container>
      </section>

      {/* Testimonials */}
      {data.testimonials.length > 0 && (
        <Container className="py-20 lg:py-28">
          <Reveal><SectionHeading eyebrow="Proof" title="Trusted by families and case managers" /></Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {data.testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <figure className="border-l-2 border-gold pl-6">
                  <Quote className="w-8 h-8 text-gold/40" />
                  <blockquote className="mt-3 text-[17px] leading-[1.7]">{t.quote}</blockquote>
                  <div className="flex gap-0.5 mt-4">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-gold text-gold" />)}</div>
                  <figcaption className="mt-3"><span className="font-heading font-semibold block">{t.name}</span><span className="text-sm text-muted-foreground">{t.role}</span></figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </Container>
      )}

      {/* Service areas */}
      <section className="bg-card border-y border-border">
        <Container className="py-16 flex flex-wrap items-center justify-between gap-6">
          <div>
            <Eyebrow>Where We Serve</Eyebrow>
            <h2 className="font-heading text-3xl font-bold mt-2">Proudly serving {areas.join(", ")}</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {areas.map((a) => <span key={a} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[15px] font-medium"><MapPin className="w-4 h-4 text-primary" /> {a}, OH</span>)}
            <Link to="/service-areas"><button className="rounded-full bg-secondary font-semibold px-5 py-2 text-[15px] hover:bg-accent transition-colors">See all areas</button></Link>
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
}
